import React, { useContext, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SidebarContext from '../../../contexts/sidebar';
import { Tooltip } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * One sidebar entry: a link, or a parent that expands its children.
 *
 * What changed and why:
 *
 * - The active item is derived from the URL, not from `currentPage`. That was a
 *   translated title kept in sessionStorage, so reloading on a child route or
 *   arriving by deep link left nothing marked, expanding a parent marked it as
 *   the current page without navigating anywhere, and switching language broke
 *   the comparison outright.
 * - Children get an active state at all. They had none: no class, no
 *   `aria-current`.
 * - A parent whose subtree holds the current route starts expanded.
 * - A parent with exactly one child renders as a direct link. An accordion that
 *   reveals a single row is pure friction.
 * - Collapsed, the label is a Radix tooltip on a portal. The old one was
 *   absolutely positioned inside a rail that clips its overflow, so about five
 *   pixels of it were visible.
 * - The floating panel the collapsed state used to render was unreachable:
 *   `handleMenuOption` expanded the sidebar before toggling, so by the next
 *   render the inline branch always won.
 */

const rowBase =
    'group relative flex w-full items-center gap-3 rounded-lg px-3 ' +
    'h-10 text-sm font-medium text-sidebar-foreground no-underline ' +
    'transition-colors duration-150 hover:no-underline ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
    'focus-visible:ring-offset-1 focus-visible:ring-offset-sidebar cursor-pointer';

// Hover sits below the active fill so the two stop looking identical.
const rowIdle = 'hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground';

const rowActive =
    'bg-sidebar-accent font-semibold text-sidebar-accent-foreground';

const DASHBOARD_ROOT = '/dashboard';

/**
 * Trailing-slash-insensitive prefix match, so /dashboard/packages covers its
 * children — except for the dashboard root itself, which prefixes every route in
 * the app and would otherwise mark Home as current on every single screen.
 */
const routeMatches = (pathname, target) => {
    if (!target) return false;
    const a = pathname.replace(/\/+$/, '').toLowerCase();
    const b = target.replace(/\/+$/, '').toLowerCase();
    if (b === DASHBOARD_ROOT) return a === b;
    return a === b || a.startsWith(`${b}/`);
};

const Submenu = ({ item }) => {
    const sidebarContext = useContext(SidebarContext);
    const { pathname } = useLocation();
    const isOpen = sidebarContext.sidebarOpen;

    // A parent with a single child is that child.
    const children = item.subNav?.length ? item.subNav : null;
    const promoted = children?.length === 1 ? children[0] : null;
    const effective = promoted ? { ...item, path: promoted.path, subNav: null } : item;

    // Most specific sibling wins. Children are not disjoint: "List of incidents"
    // points at /dashboard/incidents, which prefixes /dashboard/incidents/register,
    // so a plain match marked both of them as the current page at once.
    const activeChildPath = useMemo(() => {
        let best = null;
        for (const child of children || []) {
            if (!routeMatches(pathname, child.path)) continue;
            if (best === null || (child.path || '').length > best.length) best = child.path || '';
        }
        return best;
    }, [children, pathname]);

    const childActive = activeChildPath !== null;
    // A parent is highlighted for its own route or any of its children's.
    const isCurrent = routeMatches(pathname, effective.path) || (!promoted && childActive);

    const [expanded, setExpanded] = useState(childActive);

    const closeOnMobile = () => {
        if (sidebarContext.isMobile && sidebarContext.sidebarOpen) sidebarContext.closeSidebar();
    };

    const handleParentClick = (e) => {
        e.preventDefault();
        // Collapsed, there is nowhere to show the children: open the rail first.
        if (!isOpen && !sidebarContext.isMobile) {
            sidebarContext.showSidebar(true);
            setExpanded(true);
            return;
        }
        setExpanded((value) => !value);
    };

    const rowInner = (
        <>
            <span className="flex size-5 shrink-0 items-center justify-center [&>svg]:size-[18px]">
                {effective.icon}
            </span>
            <span className={cn('flex-1 truncate text-left', !isOpen && 'hidden')}>
                {effective.title}
            </span>
            {effective.subNav && isOpen && (
                <span
                    className={cn(
                        'flex size-4 shrink-0 items-center justify-center text-muted-foreground',
                        'transition-transform duration-200 [&>svg]:size-4',
                        expanded && 'rotate-180'
                    )}
                >
                    {effective.iconClosed}
                </span>
            )}
            {/* The brand rail. A real element, not a `before:` pseudo — those
                compile to `content: var(--content)`, which only exists if
                Preflight declares it. */}
            {isCurrent && (
                <span
                    aria-hidden="true"
                    className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-primary"
                />
            )}
        </>
    );

    const row = !effective.subNav ? (
        <Link
            className={cn(rowBase, rowIdle, !isOpen && 'justify-center', isCurrent && rowActive)}
            to={effective.path}
            onClick={closeOnMobile}
            aria-current={isCurrent ? 'page' : undefined}
        >
            {rowInner}
        </Link>
    ) : (
        <button
            type="button"
            className={cn(rowBase, rowIdle, !isOpen && 'justify-center', isCurrent && rowActive)}
            onClick={handleParentClick}
            aria-expanded={expanded}
        >
            {rowInner}
        </button>
    );

    return (
        <div className="relative">
            {isOpen ? row : <Tooltip label={effective.title}>{row}</Tooltip>}

            {isOpen && expanded && effective.subNav && (
                <ul
                    // The rail falls on the centre of the parent icon: 12px of row
                    // padding + 10px to the middle of a 20px icon.
                    className="my-1 ml-[22px] list-none space-y-0.5 border-l border-sidebar-border pl-0 animate-fade-in"
                >
                    {effective.subNav.map((child) => {
                        const active = child.path === activeChildPath;
                        return (
                            <li key={child.path || child.title}>
                                <Link
                                    className={cn(
                                        'relative flex h-8 items-center truncate rounded-md pl-6 pr-3',
                                        'text-[13px] no-underline transition-colors hover:no-underline',
                                        active
                                            ? 'font-medium text-foreground'
                                            : 'text-muted-foreground hover:text-sidebar-accent-foreground'
                                    )}
                                    to={child.path}
                                    onClick={closeOnMobile}
                                    aria-current={active ? 'page' : undefined}
                                >
                                    {active && (
                                        <span
                                            aria-hidden="true"
                                            className="absolute -left-[3px] top-1/2 size-[5px] -translate-y-1/2 rounded-full bg-primary"
                                        />
                                    )}
                                    {child.title}
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default Submenu;
